import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { SILENCE_HTTP_ERRORS } from './http-context';
import { NotificationsService } from './notifications.service';

/**
 * Global HTTP error interceptor.
 *
 * Replaces the per-call `catchError(this.handleError)` chain that the legacy
 * DataService duplicated across nineteen API method wrappers. Centralising
 * the error surface here gives :
 *  - one structured console payload per failed request (url, method, status,
 *    response body) for ops debugging ;
 *  - one user-facing snackbar via NotificationsService with i18n'd messages
 *    keyed off the HTTP status ;
 *  - a deterministic redirect to `/forbidden` on 403 so guards keep their
 *    contract even when a route was bypassed at link-time.
 *
 * 401 handling is intentionally NOT a router redirect here. The
 * keycloak-angular `includeBearerTokenInterceptor` will re-attach a fresh
 * token on the next request after refresh; redirecting from here would
 * create a loop on Keycloak's own preflight calls. The snackbar message
 * still informs the user that the session expired.
 *
 * The interceptor always rethrows the error so consumers may still react
 * locally (e.g., flipping a page-level error signal to render a banner +
 * retry button). The toast and the page banner are not duplicates: the
 * toast is a transient signal, the banner is the persistent state of the
 * failed page.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const notifications = inject(NotificationsService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      console.error('[HTTP]', {
        url: req.urlWithParams,
        method: req.method,
        status: error.status,
        statusText: error.statusText,
        error: error.error,
      });

      // Un appelant qui a marque sa requete affiche son propre message, ou aucun
      // s'il s'agit d'une sonde dont l'echec est attendu. Le journal console
      // reste emis dans tous les cas : taire le bandeau ne doit pas effacer la
      // trace pour l'exploitation.
      if (!req.context.get(SILENCE_HTTP_ERRORS)) {
        notifications.showError(error);
      }

      if (error.status === 403) {
        router.navigate(['/forbidden']);
      }

      return throwError(() => error);
    }),
  );
};
